// Shipping tab: weight and dimensions (the product's fields, all languages),
// packaging weight, free shipping, delivery days, and the delivery methods
// the product allows (DeliveryMethods: the company's, or a choice of them).
import React from "react"
import Typography from "aldoo-ra/Typography"
import { Field, TextInput, Select } from "../../../AdminShared/ui"
import CountriesSelect, { countryName } from "../../../AdminShared/CountriesSelect"
import { Switch, AdornedInput, HelperText } from "../shared/shared"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import DeliveryMethods, { shipsPhysically } from "./DeliveryMethods"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

const SHIPS_TO_MODES = [
  { value: "company", label: "Company default" },
  { value: "worldwide", label: "Worldwide" },
  { value: "countries", label: "Selected countries only" },
]
// the company's setting, in words, for the helper under the product's choice
const describe = (shipsTo, t = (s) => s) => (shipsTo?.mode === "countries" && shipsTo.countries?.length ? shipsTo.countries.map(countryName).join(", ") : t("worldwide"))

const isMap = (value) => value && typeof value === "object" && !Array.isArray(value)

export default function ShippingTab({ pid, editor, shouldShowField, isProductEditor, languages, contentType, companyDeliveryDays, companyReturnDays, companyShipsTo }) {
  const t = useProductEditorTexts()
  const { read, set, language } = editor
  const shipsTo = read("shipsTo") || { mode: "company", countries: [] }
  const setShipsTo = (patch) => set("shipsTo", { mode: shipsTo.mode || "company", countries: shipsTo.countries || [], ...patch })
  // The weight and the dimensions are the product's fields ("weight",
  // "dimensions" — what the store page lists and what the shipping cost is
  // computed from, commerce/lib/shipping), not a copy of their own: this tab
  // reads the same map the Information tab's fields editor does, and a value
  // typed here goes into it, in every language, so the two never disagree. A
  // product without the field yet (a new one) gets it here.
  const fields = read("fields")
  const current = isMap(fields) ? fields : {}
  const fieldsId = (() => {
    const id = editor.product?.fields
    if (!id) return `Product-fields-${pid}`
    return typeof id === "object" ? id?._id : id
  })()
  const setField = (key, value) => {
    for (const { code } of languages) {
      const existing = code === language ? current : editor.text(fieldsId, code)
      const map = { ...(isMap(existing) ? existing : {}), [key]: value }
      // the current language goes through `set`, which gives a new product its
      // field set; the others straight into the localization entry
      if (code === language) set("fields", map)
      else editor.setText(fieldsId, map, code)
    }
  }
  return (
    <div className="flex flex-col gap-4">
      <div>
        <Typography component="h4" variant="admin_h4" className="my-0 mb-2 inline-flex items-center gap-1.5">{t`Shipping Information`}<HelpHint helpKey="vendor.product.setup#shipping" /></Typography>
        <p className="text-sm text-admin_text_secondary my-0">{t`Set shipping details for physical products`}</p>
      </div>
      {shouldShowField("weight") && (
        <Field label={<span className="inline-flex items-center gap-1.5">{t`Weight`}<HelpHint helpKey="vendor.product.setup#weight" /></span>} className="md:w-1/3">
          <AdornedInput
            adornment={t`grams`}
            type="number"
            value={current.weight ?? ""}
            disabled={!isProductEditor}
            onChange={(v) => setField("weight", v === "" ? "" : String(parseInt(v) || 0))}
            data-testid="field-weight"
          />
          <HelperText>{t`The product's "weight" field (Information → Fields): the shipping cost is computed from it.`}</HelperText>
        </Field>
      )}
      {shouldShowField("weight") && (
        <Field label={t`Dimensions`} className="md:w-1/3">
          <TextInput value={current.dimensions ?? ""} disabled={!isProductEditor} onChange={(v) => setField("dimensions", v)} placeholder="130x200 mm" data-testid="field-dimensions" />
          <HelperText>{t`The product's "dimensions" field, as the store page lists it.`}</HelperText>
        </Field>
      )}
      {shouldShowField("packagingWeight") && (
        <Field label={t`Packaging Weight`} className="md:w-1/3">
          <AdornedInput adornment={t`grams`} type="number" value={read("packagingWeight") || ""} disabled={!isProductEditor} onChange={(v) => set("packagingWeight", parseInt(v) || 0)} data-testid="field-packaging-weight" />
        </Field>
      )}
      {shouldShowField("free_shipping") && <Switch checked={!!read("free_shipping")} onChange={(v) => set("free_shipping", v)} label={t`Free Shipping`} data-testid="switch-free-shipping" />}
      <Field label={<span className="inline-flex items-center gap-1.5">{t`Ships to`}<HelpHint helpKey="vendor.product.setup#ships-to" /></span>} className="md:w-1/3">
        <Select value={shipsTo.mode || "company"} options={SHIPS_TO_MODES.map((o) => ({ ...o, label: t(o.label) }))} onChange={(mode) => setShipsTo({ mode })} disabled={!isProductEditor} className="w-full" data-testid="field-ships-to-mode" />
        <HelperText>{t("Company default: %s. Override here for this specific product.", describe(companyShipsTo, t))}</HelperText>
      </Field>
      {shipsTo.mode === "countries" && (
        <Field label={t`Countries`} className="md:w-1/2">
          <CountriesSelect value={shipsTo.countries || []} onChange={(countries) => setShipsTo({ countries })} disabled={!isProductEditor} data-testid="field-ships-to-countries" />
          <HelperText>{t`Until one is picked, the product ships worldwide.`}</HelperText>
        </Field>
      )}
      <Field label={<span className="inline-flex items-center gap-1.5">{t`Delivery Days`}<HelpHint helpKey="vendor.product.setup#delivery-days" /></span>} className="md:w-1/3">
        <TextInput type="number" min={1} value={read("deliveryDays") ?? companyDeliveryDays ?? 3} disabled={!isProductEditor} onChange={(v) => set("deliveryDays", parseInt(v) || null)} data-testid="field-delivery-days" />
        <HelperText>{t("Company default: %s days. Override here for this specific product.", companyDeliveryDays || 3)}</HelperText>
      </Field>
      {/* how long a buyer has to ask to return it, from delivery: the company's unless set (never under 14) */}
      <Field label={t`Return Days`} className="md:w-1/3">
        <TextInput type="number" min={14} value={read("returnDays") ?? ""} placeholder={String(companyReturnDays || 14)} disabled={!isProductEditor} onChange={(v) => set("returnDays", v === "" ? null : Math.max(14, parseInt(v) || 14))} data-testid="field-return-days" />
        <HelperText>{t("Company default: %s days. Leave empty to use it; at least 14 by law.", companyReturnDays || 14)}</HelperText>
      </Field>
      {/* a download is not delivered: no courier to choose */}
      {shipsPhysically(contentType) && <DeliveryMethods editor={editor} isProductEditor={isProductEditor} />}
    </div>
  )
}
